import { useEffect, useState } from "react";
import { CaretLeft, CaretRight, Pause, Play, X } from "@phosphor-icons/react";
import { photoSrc } from "@/components/Thumb";

export const Slideshow = ({ items, start = 0, onClose }) => {
  const [idx, setIdx] = useState(start);
  const [playing, setPlaying] = useState(true);

  const next = () => setIdx((i) => (i + 1) % items.length);
  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [playing, items.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const item = items[idx];

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/95"
      data-testid="completion-slideshow"
    >
      <div className="flex items-center gap-3 border-b border-zinc-800 px-4 py-3">
        <span className="text-xs uppercase tracking-widest text-zinc-500">
          {idx + 1} / {items.length}
        </span>
        <button
          data-testid="slideshow-toggle-play"
          onClick={() => setPlaying((p) => !p)}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-800 text-zinc-300 hover:text-white"
        >
          {playing ? (
            <Pause size={16} weight="bold" />
          ) : (
            <Play size={16} weight="bold" />
          )}
        </button>
        <button
          data-testid="slideshow-close"
          onClick={onClose}
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-800 text-zinc-300 hover:text-white"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center p-4">
        <button
          data-testid="slideshow-prev"
          onClick={prev}
          className="absolute left-4 flex h-11 w-11 items-center justify-center rounded-full border border-zinc-700 bg-black/60 text-zinc-200 hover:text-white"
        >
          <CaretLeft size={18} weight="bold" />
        </button>
        <img
          key={item.id}
          src={photoSrc(item.photo)}
          alt={item.name || item.slab_color}
          data-testid="slideshow-image"
          className="max-h-[72vh] max-w-full animate-in fade-in object-contain duration-500"
        />
        <button
          data-testid="slideshow-next"
          onClick={next}
          className="absolute right-4 flex h-11 w-11 items-center justify-center rounded-full border border-zinc-700 bg-black/60 text-zinc-200 hover:text-white"
        >
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      <div
        className="border-t border-zinc-800 px-6 py-4 text-center"
        data-testid="slideshow-caption"
      >
        <h3 className="text-xl font-bold text-zinc-50">
          {item.name || item.slab_color || "Finished project"}
        </h3>
        <p className="mt-1 flex flex-wrap items-center justify-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
          {item.category && (
            <span className="rounded-full border border-indigo-400/40 bg-indigo-500/10 px-2 py-0.5 text-indigo-300">
              {item.category}
            </span>
          )}
          {item.slab_color && <span>{item.slab_color}</span>}
          {item.job_code && <span>{item.job_code}</span>}
        </p>
        {item.description && (
          <p className="mt-2 text-sm text-zinc-400">{item.description}</p>
        )}
      </div>
    </div>
  );
};
